export interface BroadcastChannelPort<T> {
  post: (message: T) => void;
  subscribe: (listener: (message: T) => void) => () => void;
  close: () => void;
}

const noopChannel = <T>(): BroadcastChannelPort<T> => ({
  post: () => undefined,
  subscribe: () => () => undefined,
  close: () => undefined,
});

export const createBroadcastChannel = <T>(
  name: string,
  parse: (value: unknown) => T | undefined,
): BroadcastChannelPort<T> => {
  if (typeof BroadcastChannel !== "function") {
    return noopChannel();
  }

  const channel = new BroadcastChannel(name);
  const listeners = new Set<(message: T) => void>();
  const onMessage = (event: MessageEvent<unknown>) => {
    const message = parse(event.data);
    if (!message) {
      return;
    }
    for (const listener of [...listeners]) {
      listener(message);
    }
  };
  channel.addEventListener("message", onMessage);

  const post = (message: T) => {
    try {
      channel.postMessage(message);
    } catch {
      // Failed posts stay local rather than throwing at the call site.
    }
  };

  const subscribe = (listener: (message: T) => void) => {
    listeners.add(listener);
    return () => listeners.delete(listener);
  };

  const close = () => {
    listeners.clear();
    channel.removeEventListener("message", onMessage);
    channel.close();
  };

  return {
    post,
    subscribe,
    close,
  };
};
